import posthog from 'posthog-js';
import type { CourseType as CourseTypeValue } from './courseService';

/**
 * PostHog analytics service.
 *
 * - `initAnalytics()` initializes posthog-js with property sanitization that
 *   scrubs sensitive URL parameters (Clerk invitation JWTs, background-check
 *   verification ids) before events leave the browser.
 * - `track()` is a typed wrapper around `posthog.capture()` so all custom
 *   event names and properties live in one place.
 *
 * Event naming convention: snake_case, past tense (e.g. `course_registered`).
 */

// ---------------------------------------------------------------------------
// Sensitive data scrubbing
// ---------------------------------------------------------------------------

/** Query/hash params that must never reach analytics (live tokens). */
const SENSITIVE_PARAMS = ['__clerk_ticket', '__clerk_status', 'ticket', 'token'];

/** Scrub sensitive params from a URL-ish string (query string and hash fragment). */
export function scrubSensitiveParams(value: string): string {
  let result = value;
  for (const param of SENSITIVE_PARAMS) {
    result = result.replace(new RegExp(`([?&#])${param}=[^&#]*`, 'gi'), `$1${param}=REDACTED`);
  }
  // Background-check redirect ids on the individual-profile page
  if (result.includes('/auth/individual-profile')) {
    result = result.replace(/([?&])id=[^&#]*/gi, '$1id=REDACTED');
  }
  return result;
}

type AnyProps = Record<string, unknown>;

function scrubValue(value: unknown): unknown {
  if (typeof value === 'string' && value.includes('=')) {
    return scrubSensitiveParams(value);
  }
  if (Array.isArray(value)) {
    return value.map(scrubValue);
  }
  if (value && typeof value === 'object') {
    return scrubProperties(value as AnyProps);
  }
  return value;
}

function scrubProperties(properties: AnyProps): AnyProps {
  for (const key of Object.keys(properties)) {
    properties[key] = scrubValue(properties[key]);
  }
  return properties;
}

// ---------------------------------------------------------------------------
// Initialization / identity
// ---------------------------------------------------------------------------

export function initAnalytics(key: string | undefined, host: string | undefined) {
  if (!key) return;
  posthog.init(key, {
    api_host: host ?? 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    sanitize_properties: (properties) => scrubProperties(properties),
  });
}

/** Domains whose users are flagged `is_internal` for test-account filtering. */
const INTERNAL_EMAIL_DOMAINS = ['picpa.org', 'cpacrossings.com'];

export function identifyUser(
  userId: string,
  props: { email?: string; name?: string }
) {
  const domain = props.email?.split('@')[1]?.toLowerCase();
  posthog.identify(userId, {
    ...props,
    is_internal: domain ? INTERNAL_EMAIL_DOMAINS.includes(domain) : false,
  });
}

/** Enrich the person profile once the backend profile is available. */
export function setUserProfileProperties(props: {
  role?: string;
  stateCode?: string;
  tenantId?: string;
}) {
  posthog.setPersonProperties({
    role: props.role,
    state_code: props.stateCode,
    tenant_id: props.tenantId,
  });
}

/** Associate the session with a tenant group, optionally enriching group properties. */
export function setTenantGroup(tenantId: string, properties?: { name?: string }) {
  if (properties) {
    posthog.group('tenant', tenantId, properties);
  } else {
    posthog.group('tenant', tenantId);
  }
}

export function resetAnalytics() {
  posthog.reset();
}

// ---------------------------------------------------------------------------
// Typed custom events
// ---------------------------------------------------------------------------

export type SignupType = 'individual' | 'business';

/** All custom events and their required properties. */
interface EventMap {
  // Signup / onboarding
  signup_started: { signup_type: SignupType };
  signup_completed: { signup_type: SignupType };
  business_setup_completed: void;
  invite_accepted: void;
  background_info_completed: { method: 'automatic' | 'manual' };
  onboarding_completed: { license_count: number };
  onboarding_skipped: { step: number };
  // Billing
  plan_viewed: { plan_type: SignupType };
  checkout_started: { plan_type: SignupType; item_price_id?: string };
  // Learning
  course_searched: { query: string; result_count: number };
  course_viewed: { course_id: string; course_type: CourseTypeValue };
  course_registered: { course_id: string; course_type: CourseTypeValue };
  course_launched: { course_id: string };
  // `course_completed` is NOT sent from the browser: completions are detected on the backend
  // (Docebo webhook, CPAx credit sync, tenant-admin status update) and captured server-side by
  // Ascend.Core.Api/Services/Analytics/PostHogAnalyticsService.cs with the same distinct_id (Clerk user id).
  certificate_downloaded: { course_id?: string };
  course_bookmarked: { course_id: string; bookmarked: boolean };
  external_credits_imported: { source: string; created: number; updated: number };
  // Spreadsheet credit import (no file names, emails or titles — counts only)
  credit_import_parsed: {
    mode: 'self' | 'tenant';
    /** Profile the file was read as ('template' | 'lcvista' | 'other') and what the parser detected, if anything. */
    source: string;
    detected_source: string | null;
    rows: number;
    errors: number;
    duplicates: number;
    unrecognized_columns: number;
  };
  credit_import_committed: { mode: 'self' | 'tenant'; rows_imported: number; rows_skipped: number; fixes_made: number };
  credit_import_abandoned: { mode: 'self' | 'tenant'; rows: number; errors_remaining: number };
  credit_import_undone: { mode: 'self' | 'tenant'; rows_removed: number; minutes_since_import: number };
  // Tenant admin
  user_invited: { role: string; count: number };
  compliance_report_run: void;
  compliance_report_csv_export: void;
}

export function track<E extends keyof EventMap>(
  event: E,
  ...args: EventMap[E] extends void ? [] : [EventMap[E]]
) {
  posthog.capture(event, args[0] ?? undefined);
}
